/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useMemo, useState } from 'react';

import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import IconButton from '@atlaskit/button/icon/button';
import {
	redo as redoKeymap,
	ToolTipContent,
	undo as undoKeymap,
} from '@atlaskit/editor-common/keymaps';
import type { EditorAppearance } from '@atlaskit/editor-common/types';
import { token } from '@atlaskit/tokens';
import type { PromptContentChangePayload } from '@atlassian/conversation-assistant-chat-prompt-input/prompt-content-change-payload';
import { PromptScreen } from '@atlassian/generative-ai-modal/screens/Prompt';
import type { PromptEditorProps } from '@atlassian/generative-ai-modal/screens/UserInputCommandPalette';

import { isReviewMomentEnabledNoExposure } from '../../../../utils/review-moment-enabled';
import { RovoPromptScreenWithLogic } from '../../../ExperienceApplication/screens-with-logic/RovoPromptScreenWithLogic';
import { ActionButtons } from './ActionButtons';
import {
	renderMinimisePromptIcon,
	renderRedoIcon,
	renderUndoIcon,
	renderViewChangesIcon,
} from './icons';
import { messages } from './messages';

// The embedded prompt renders frameless, which zeroes the padding the standard
// prompt surface applies. Re-applying it here keeps the PSR input height in line
// with the standard prompt.
const askRovoCSS = css({
	flex: 1,
	minWidth: 0,
	display: 'flex',
	alignItems: 'flex-start',
	paddingInline: token('space.200'),
	paddingBlock: token('space.150'),
});

const askRovoWithAttachmentRailCSS = css({
	flexDirection: 'column',
});

const askRovoTopRowCSS = css({
	flex: 1,
	width: '100%',
	minWidth: 0,
	display: 'flex',
	alignItems: 'flex-start',
	gap: token('space.100'),
});

const attachmentRailCSS = css({
	width: '100%',
	minWidth: 0,
});

const promptSlotCSS = css({
	flex: 1,
	minWidth: 0,
});

const collapsedPromptSlotWidth = '140px';

const promptSlotCollapsedCSS = css({
	flex: 'none',
	width: collapsedPromptSlotWidth,
});

// Keeps collapsible controls mounted for their exit animation. Delayed `visibility` then removes
// hidden controls from pointer and keyboard interaction.
const collapsibleActionWrapperCSS = css({
	display: 'grid',
	flexShrink: 0,
	transitionProperty: 'grid-template-columns, opacity, transform, visibility',
	transitionDuration: '0.2s, 0.12s, 0.2s, 0s',
	transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
	'@media (prefers-reduced-motion: reduce)': {
		transitionDuration: '0s',
		transitionDelay: '0s',
	},
});

const rightActionsVisibleCSS = css({
	gridTemplateColumns: '1fr',
	opacity: 1,
	transform: 'translateX(0)',
	visibility: 'visible',
	transitionDelay: '0s, 0.08s, 0.08s, 0s',
});

const rightActionsHiddenCSS = css({
	gridTemplateColumns: '0fr',
	opacity: 0,
	transform: 'translateX(-4px)',
	visibility: 'hidden',
	pointerEvents: 'none',
	// Keep the controls visible while their exit animation runs.
	transitionDelay: '0s, 0s, 0s, 0.2s',
});

const leadingActionCSS = css({
	display: 'flex',
	alignItems: 'center',
	overflow: 'hidden',
	minWidth: 0,
});

// Clips controls as their grid column collapses.
const rightActionsCSS = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.050'),
	overflow: 'hidden',
	minWidth: 0,
});

export interface AskRovoProps {
	appearance: EditorAppearance;
	cancel: (_options?: {
		shouldClearSelectionContext?: boolean;
		shouldIgnoreNextSpaceTrigger?: boolean;
	}) => void;
	canGoNext: boolean;
	canGoPrevious: boolean;
	canRedo: boolean;
	canUndo: boolean;
	canViewChanges: boolean;
	hasFallbackPrompt: boolean;
	/**
	 * Indicates text, a mention, or an inline card. Attachment-only drafts retain
	 * review controls until the user begins composing text.
	 */
	hasInputText: boolean;
	hasRovoPrompt: boolean;
	/** Hide the diff toggle while keeping Undo/Redo and step-through controls. */
	hideViewChanges?: boolean;
	isAIFC: boolean | undefined;
	isCollapsiblePrompt: boolean;
	isPromptExpanded: boolean;
	isStepThroughExpanded: boolean;
	onMinimisePrompt: () => void;
	onNext: () => void;
	onPrevious: () => void;
	/** Reports prompt content so the parent can track drafts without reading editor DOM. */
	onPromptContentChange: (payload: PromptContentChangePayload) => void;
	onRedo: () => void;
	onTextChange: (input: string) => void;
	onToggleStepThrough: () => void;
	onUndo: () => void;
	onViewChanges: () => void;
	openInRovoChat: boolean | undefined;
	openInRovoChatConfig: { onBeforeSubmit: (prompt: DocNode) => boolean };
	promptControlsRef: React.Ref<{ clear: () => void }>;
	PromptEditor: ((props: PromptEditorProps) => JSX.Element) | undefined;
	segmentIndex: number;
	/**
	 * Whether DELETED content is currently revealed in the always-on diff overlay.
	 * `false` = deletions hidden (default review view); `true` = deletions shown.
	 * Drives the show/hide-changes toggle's icon, label and selected state.
	 */
	showDeletedChanges: boolean;
	submit: () => void;
	textInput: string;
	totalSegments: number;
}

/**
 * Renders the Review Moment prompt with its review controls. Attachment-only drafts retain
 * review controls; text composition transitions to the prompt's own actions.
 */
export function AskRovo({
	hasRovoPrompt,
	hasFallbackPrompt,
	hasInputText,
	isPromptExpanded,
	openInRovoChat,
	openInRovoChatConfig,
	cancel,
	PromptEditor,
	textInput,
	onTextChange,
	onPromptContentChange,
	submit,
	appearance,
	isAIFC,
	canUndo,
	canRedo,
	onUndo,
	onRedo,
	canViewChanges,
	showDeletedChanges,
	onViewChanges,
	totalSegments,
	segmentIndex,
	canGoPrevious,
	canGoNext,
	onPrevious,
	onNext,
	isStepThroughExpanded,
	hideViewChanges = false,
	onToggleStepThrough,
	isCollapsiblePrompt,
	onMinimisePrompt,
	promptControlsRef,
}: AskRovoProps): JSX.Element {
	const intl = useIntl();
	const [attachmentListMountPoint, setAttachmentListMountPoint] = useState<HTMLDivElement | null>(
		null,
	);

	// [go/atlassian-slack/C0BH7H22PLH/p1788409204857639] Under Review moment the
	// view-changes toggle's tooltip/label copy is simplified to "View changes" / "Hide
	// changes" (see `messages.ts` — the message ids keep their historical `M1` suffix).
	// No-exposure variant: this is a render path and the canonical exposure is already
	// logged at the Review moment gate-enforcement points.
	const usesSimplifiedViewChangesCopy = isReviewMomentEnabledNoExposure();

	// Custom tooltip content appending the keyboard shortcut to the Undo/Redo
	// labels (e.g. "Undo ⌘Z"). Memoized so the `tooltip` prop object is stable
	// across renders (`@atlassian/perf-linting/no-unstable-inline-props`).
	const undoTooltip = useMemo(
		() => ({
			content: (
				<ToolTipContent description={intl.formatMessage(messages.undo)} keymap={undoKeymap} />
			),
		}),
		[intl],
	);
	const redoTooltip = useMemo(
		() => ({
			content: (
				<ToolTipContent description={intl.formatMessage(messages.redo)} keymap={redoKeymap} />
			),
		}),
		[intl],
	);

	// Render the Rovo prompt unless we have a fallback editor to show.
	const shouldRenderFallbackPrompt =
		!(openInRovoChat && hasRovoPrompt) && hasFallbackPrompt && !!PromptEditor;

	return (
		<div
			css={[askRovoCSS, isCollapsiblePrompt && askRovoWithAttachmentRailCSS]}
			data-testid="post-stream-review-ask-rovo"
		>
			<div css={askRovoTopRowCSS}>
				{/* Kept mounted for the exit animation. */}
				{isCollapsiblePrompt && !shouldRenderFallbackPrompt && (
					<div
						css={[
							collapsibleActionWrapperCSS,
							isPromptExpanded ? rightActionsVisibleCSS : rightActionsHiddenCSS,
						]}
						aria-hidden={!isPromptExpanded || undefined}
						data-testid="post-stream-review-minimise-prompt-wrapper"
					>
						<div css={leadingActionCSS}>
							<IconButton
								appearance="subtle"
								icon={renderMinimisePromptIcon}
								label={intl.formatMessage(messages.minimisePrompt)}
								isTooltipDisabled={false}
								onClick={onMinimisePrompt}
								testId="post-stream-review-minimise-prompt"
							/>
						</div>
					</div>
				)}
				<div
					css={[promptSlotCSS, isCollapsiblePrompt && !isPromptExpanded && promptSlotCollapsedCSS]}
				>
					{shouldRenderFallbackPrompt && PromptEditor ? (
						<PromptScreen
							textInput={textInput}
							onTextChange={onTextChange}
							submit={submit}
							cancel={cancel}
							PromptEditor={PromptEditor}
							appearance={appearance}
							isAIFC={isAIFC}
						/>
					) : (
						<RovoPromptScreenWithLogic
							cancel={cancel}
							openInRovoChatConfig={hasRovoPrompt ? openInRovoChatConfig : undefined}
							autoClose={false}
							isPostStreamReview={true}
							isCollapsiblePrompt={isCollapsiblePrompt}
							onPromptContentChange={onPromptContentChange}
							rovoInlineChatAppearance="frameless"
							showTrailingActions={hasInputText}
							keepTrailingActionsInline={isCollapsiblePrompt}
							controlsRef={promptControlsRef}
							attachmentListMountPoint={isCollapsiblePrompt ? attachmentListMountPoint : undefined}
						/>
					)}
				</div>
				<div
					css={[
						collapsibleActionWrapperCSS,
						hasInputText ? rightActionsHiddenCSS : rightActionsVisibleCSS,
					]}
					aria-hidden={hasInputText || undefined}
					data-testid="post-stream-review-controls"
				>
					<div css={rightActionsCSS}>
						<ActionButtons
							totalSegments={totalSegments}
							segmentIndex={segmentIndex}
							canGoPrevious={canGoPrevious}
							canGoNext={canGoNext}
							isStepThroughExpanded={isStepThroughExpanded}
							onPrevious={onPrevious}
							onNext={onNext}
							onToggleStepThrough={onToggleStepThrough}
						/>
						{/* IconButton tooltips are opt-in (`isTooltipDisabled` defaults to true). These
				    controls surface their `label` on hover — including the view-changes toggle's
				    "Compare with original"/"Only show suggestion" states (or, under the M1 experience,
				    "View changes"/"Hide changes" — see `usesSimplifiedViewChangesCopy` above). Undo/Redo pass a
				    custom tooltip `content` that appends the keyboard shortcut (e.g. "Undo ⌘Z") via
				    `ToolTipContent`, while `label` stays the plain accessible name. Shortcuts handled in
				    `useReviewKeyboardShortcuts` (hooks.ts) — keep the two in sync. */}
						{!hideViewChanges && (
							<IconButton
								appearance="subtle"
								icon={renderViewChangesIcon}
								label={intl.formatMessage(
									usesSimplifiedViewChangesCopy
										? showDeletedChanges
											? messages.hideChangesM1
											: messages.viewChangesM1
										: showDeletedChanges
											? messages.hideChanges
											: messages.viewChanges,
								)}
								isTooltipDisabled={false}
								isSelected={showDeletedChanges}
								onClick={onViewChanges}
								isDisabled={!canViewChanges}
								testId="post-stream-review-view-changes"
							/>
						)}
						<IconButton
							appearance="subtle"
							icon={renderUndoIcon}
							label={intl.formatMessage(messages.undo)}
							isTooltipDisabled={false}
							tooltip={undoTooltip}
							isDisabled={!canUndo}
							onClick={onUndo}
							testId="post-stream-review-undo"
						/>
						<IconButton
							appearance="subtle"
							icon={renderRedoIcon}
							label={intl.formatMessage(messages.redo)}
							isTooltipDisabled={false}
							tooltip={redoTooltip}
							isDisabled={!canRedo}
							onClick={onRedo}
							testId="post-stream-review-redo"
						/>
					</div>
				</div>
			</div>
			{isCollapsiblePrompt && (
				<div
					ref={setAttachmentListMountPoint}
					css={attachmentRailCSS}
					data-testid="post-stream-review-attachments"
				/>
			)}
		</div>
	);
}
